import { useState } from 'react';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import Button from '../../../shared/components/Button';
import { DepositRuleFields } from './DepositRuleFields';
import type { CharterService, CreateServicePayload, DepositRule } from '../types';

interface Props {
  initial?: CharterService | null;
  onSubmit: (values: CreateServicePayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function ServiceForm({ initial, onSubmit, onCancel, isSaving }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [durationMinutes, setDurationMinutes] = useState(String(initial?.durationMinutes ?? 60));
  // Price stored in minor units on the wire; entered/displayed in major units (naira).
  const [priceMajor, setPriceMajor] = useState(initial && initial.price ? (initial.price / 100).toString() : '');
  const [depositRule, setDepositRule] = useState<DepositRule>(initial?.depositRule ?? { type: 'none' });
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const duration = parseInt(durationMinutes, 10);
    if (!Number.isFinite(duration) || duration < 1) {
      setError('Duration must be at least 1 minute');
      return;
    }
    const priceNum = priceMajor.trim() === '' ? 0 : parseFloat(priceMajor);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setError('Price cannot be negative (0 = free)');
      return;
    }
    if (depositRule.type === 'percentage') {
      const pct = depositRule.percentageValue ?? 0;
      if (pct < 1 || pct > 100) {
        setError('Deposit percentage must be between 1 and 100');
        return;
      }
      if (priceNum <= 0) {
        setError('A percentage deposit needs a price greater than zero');
        return;
      }
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      durationMinutes: duration,
      price: Math.round(priceNum * 100),
      depositRule,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Name"
        placeholder="e.g. Bespoke Design Consultation"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextArea
        label="Description"
        placeholder="What the client gets from this service"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Duration (minutes)"
          type="number"
          min={1}
          step={5}
          value={durationMinutes}
          onChange={(e) => setDurationMinutes(e.target.value)}
        />
        <Input
          label="Price (₦) — 0 for free"
          type="number"
          min={0}
          step="0.01"
          placeholder="0.00"
          value={priceMajor}
          onChange={(e) => setPriceMajor(e.target.value)}
        />
      </div>
      <DepositRuleFields value={depositRule} onChange={setDepositRule} />

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-3 pt-1">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving} loading={isSaving}>
          {initial ? 'Save changes' : 'Create service'}
        </Button>
      </div>
    </form>
  );
}
